import React, { useState, useTransition, useRef, useMemo, useEffect, useCallback } from 'react';
import { useToast } from 'use-toast-mui';
import {
  Autocomplete,
  Chip,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useDirtyTracker } from '../../../../context/UpdateContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { createConsultation, updateConsultation, deleteConsultation } from '../../../../api/consultation';
import { getDuetPartners } from '../../../../api/lesson';
import { withOverlapConfirm } from '../../../../utils/overlapConfirm';
import { filterRoomsByLessonType, pickAutoRoomId } from '../../../../constants/roomTypes';
import {
  createBranchSchedule,
  updateBranchSchedule,
  deleteBranchSchedule,
} from '../../../../api/branchSchedule';
import CommonModalTitle from '../../../../form-components/CommonModalTitle';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import {
  ErrorHighlightWrapper,
  StyledTextarea,
  ModalContainer,
  ModalHeader,
  ModalCloseIcon,
  FooterBar,
  FooterRightGroup,
  LessonTypeBar,
  LessonTypeBarLabel,
  LessonTypeChipGroup,
  LessonTypeChip,
  LessonTypeChipText,
  ContentArea,
  ContentInner,
  SectionCard,
  SectionHeader,
  FormLabel,
  RequiredMark,
} from './PrivateLessonEditModalStyled';
import {
  CONSULT_METHOD_OPTIONS,
  CONSULT_PURPOSE_OPTIONS,
  CONSULT_RESULT_OPTIONS,
} from '../../../../constants/consultation';
import { VISIT_PATH_ETC_MAX_LENGTH, VISIT_PATH_OTHER } from '../../../../constants/visitPath';
import useConsultOptions from '../../../../hooks/useConsultOptions';
import { createTemporaryUser, searchUserByPhoneNumber } from '../../../../api/temporaryUser';
import EnterSaleInfoModal from '../../../SalesManagment/modal/enterSaleInfoModal/EnterSaleInfoModal';
import { converterJsonToFormData, getChosung } from '../../../../utils/methods';
import { otTicketValue } from '../../privateLesson/lessonStatus';
import CommonDatePicker from '../../../../form-components/CommonDatePicker';
import ConfirmModal from '../../../common/modal/ConfirmModal';
import { TicketPeriodDialog, isOutOfTicketPeriod } from '../../privateLesson/ScheduleMoveConfirm';

// 키보드로 날짜를 치는 중간값(예: 2026-10-2) — 숫자 8자리가 다 차기 전엔 반영하지 않는다.
// 반영하면 부모가 'YYYY-MM-DD' 로 되돌려 그려 입력칸이 2026-10-02 로 바뀌고 마지막 숫자가 사라진다.
const isPartialDateInput = keyboardInput =>
  typeof keyboardInput === 'string' && keyboardInput.replace(/\D/g, '').length < 8;

// 수강권 만료일 표시 — 'YYYY-MM-DD'·'YYYY-MM-DDTHH:mm:ss' 모두 '만료일 YYYY.MM.DD'
const expireLabel = date => `만료일 ${String(date).slice(0, 10).replaceAll('-', '.')}`;
// 선택된 회원을 검색 입력칸 안에 칩으로 보여준다(입력칸 아래 별도 라벨 대신). onRemove 가 없으면(수정) 지울 수 없다.
const selectedMemberAdornment = (members, onRemove, rest) => (
  <>
    {members.map(member => (
      <Chip
        key={member.id}
        size="small"
        label={member.name}
        onDelete={onRemove ? () => onRemove(member.id) : undefined}
        onMouseDown={e => e.stopPropagation()}
        sx={{
          height: '24px',
          mr: '4px',
          backgroundColor: '#E6F6F7',
          color: '#1F2A37',
          fontSize: '13px',
          fontWeight: 600,
          '& .MuiChip-deleteIcon': { fontSize: '16px', color: '#4AB3BC', '&:hover': { color: '#3A9FA8' } },
        }}
      />
    ))}
    {rest}
  </>
);


// 비회원 상담은 전화번호를 받아두되, 모르는 경우가 있어 미입력이면 이 번호로 채운다.
const DEFAULT_NON_MEMBER_PHONE = '010-0000-0000';

// 같은 번호 고객 확인창에 이름을 보여줄 최대 인원
const DUPLICATE_PREVIEW_COUNT = 10;

// 상담 대상 타입 옵션
const CONSULT_TYPE_OPTIONS = [
  { value: 'MEMBER', label: '회원' },
  { value: 'TEMPORARY', label: '잠재회원' },
  { value: 'NON_MEMBER', label: '비회원' },
];



// 전화번호 포맷팅 함수 (010-0000-0000 형식)
const formatPhoneNumber = value => {
  if (!value) return '';
  const numbers = value.replace(/[^\d]/g, '');
  const limited = numbers.slice(0, 11);

  if (limited.length <= 3) {
    return limited;
  } else if (limited.length <= 7) {
    return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  } else {
    return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
  }
};

const hourOptions = Array.from({ length: 24 }, (_, index) => index);
const minuteOptions = Array.from({ length: 12 }, (_, index) => index * 5);

// 숫자 입력 + 인풋 안 -/+ 버튼(차감 횟수·OT 인원수 공용). min 미만으로는 내려가지 않는다.
const CountStepper = ({ value, onChange, min = 1, unit, label }) => {
  const current = Number(value) || min;
  return (
    <TextField
      size="small"
      type="text"
      inputMode="numeric"
      placeholder={String(min)}
      value={value ?? min}
      onChange={e => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      InputProps={{
        startAdornment: (
          <IconButton
            size="small"
            aria-label={`${label} 줄이기`}
            disabled={current <= min}
            onClick={() => onChange(Math.max(min, current - 1))}
            sx={{ p: '2px', mr: '4px' }}>
            <RemoveIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        ),
        endAdornment: (
          <>
            <span style={{ color: '#999', fontSize: '14px', marginRight: '4px' }}>{unit}</span>
            <IconButton size="small" aria-label={`${label} 늘리기`} onClick={() => onChange(current + 1)} sx={{ p: '2px' }}>
              <AddIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </>
        ),
      }}
      inputProps={{ style: { textAlign: 'center', padding: '8px 0' } }}
      sx={{ width: '150px' }}
    />
  );
};

// 강사 선택 모드(주간 패널 "+ 일정 추가" / 빈칸 클릭 생성)에서 사용하는 담당강사·담당자 Select
const TrainerSelectField = ({ trainerId, trainerOptions, onChange }) => (
  <FormControl size="small" sx={{ flex: 1 }}>
    <Select
      displayEmpty
      value={trainerId || -1}
      onChange={e => {
        const selectedId = e.target.value;
        if (selectedId === -1) {
          onChange(null, '');
          return;
        }
        const picked = trainerOptions.find(trainer => trainer.trainerId === selectedId);
        onChange(selectedId, picked?.trainerName || '');
      }}>
      <MenuItem value={-1}>담당강사를 선택해 주세요</MenuItem>
      {trainerOptions.map(trainer => (
        <MenuItem key={trainer.trainerId} value={trainer.trainerId}>
          {trainer.trainerName}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

// 시간 선택지는 0~23시라 종료·시작은 00:00~23:55 안에서만 옮긴다
const LAST_SELECTABLE_MINUTES = 23 * 60 + 55;
const toMinutes = (hour, minute) => Number(hour || 0) * 60 + Number(minute || 0);

const PrivateLessonEditModal = ({
  open,
  isUpdate,
  modalData,
  setModalData,
  rooms,
  tickets,
  searchResults,
  setSearchResults,
  selectedMembers,
  setSelectedMembers,
  isMemberChanged,
  setIsMemberChanged,
  setTickets,
  courseClassType,
  debouncedGetMembers,
  handleChangeMember,
  otMemberTickets = {},
  otTicketErrors = {},
  onRetryOtTickets,
  handleCancel,
  onConflict,
  handleSaveEvent,
  onRefresh,
  trainerOptions = [],
  trainerSelectable = false,
  // (lessonType, startHour, startMinute) => { endHour, endMinute, exerciseRoomId? } — 생성 시 구분별 기본 수업 시간·강의실
  getCreateDefaults,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { visitPaths, exercisePurposes: exercisePurposeOptions } = useConsultOptions();
  const [isPending, startTransition] = useTransition();
  const user = useSelector(state => state.user.user);
  const queryClient = useQueryClient();

  // 상담 타입 여부 확인
  const isCounselType = modalData.lessonType === 'COUNSEL';
  // 수업 수정 진입 시에는 lessonType 없이 courseClassType만 담겨오므로 둘을 합쳐서 현재 레슨구분을 판단한다.
  const currentLessonType = modalData.lessonType || modalData.courseClassType || 'PRIVATE_LESSON';
  // 기타 일정 여부 — 회원·수강권·강의실이 없는 지점 내부 일정
  const isEtcType = currentLessonType === 'ETC';
  // OT: 레슨구분 하나, 인원수(정원)로 1:1/다인 구분. 회원은 인원수까지 다중 선택, 각자 OT 수강권으로 차감.
  const isOtType = currentLessonType === 'OT';
  const otMaxMembers = Math.max(1, Number(modalData.membersMaxCount) || 1);

  // 레슨구분에 맞는 강의실만 고를 수 있게 거른다. 이미 선택된 강의실은 유형이 달라도 남긴다.
  const selectableRooms = useMemo(
    () => filterRoomsByLessonType(rooms, currentLessonType, modalData?.exerciseRoomId),
    [rooms, currentLessonType, modalData?.exerciseRoomId],
  );
  // 회원·수강권은 예약과 차감의 주체라 수정으로 바꿀 수 없다. 바꾸려 하면 이유를 알려준다.
  const notifyLockedOnUpdate = () => {
    toast.error('회원과 수강권은 수업 수정으로 변경할 수 없습니다. 수업을 삭제하고 새로 등록해주세요');
  };

  // Autocomplete 인풋값 추적
  const [autoInputValue, setAutoInputValue] = useState('');
  // 회원 목록 팝업 열림 상태 — 검색창 포커스만으로 후보 전체를 띄운다(개인·듀엣)
  const [memberListOpen, setMemberListOpen] = useState(false);
  // 담당 회원만 보기 — 모달에서 고른 강사가 배정된 수강권 보유 회원만. 기본 끔, 모달 열릴 때 초기화
  const [onlyAssignedMembers, setOnlyAssignedMembers] = useState(false);
  // 목록 항목에서 수강권을 펼쳐 본 회원 id 집합 — 기본은 첫 1개만 보이고 "외 N개 펼치기"로 연다
  const [expandedMemberIds, setExpandedMemberIds] = useState(() => new Set());
  const toggleExpandedMember = id =>
    setExpandedMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const loadMembers = (input, onlyAssigned = onlyAssignedMembers) =>
    debouncedGetMembers(input, undefined, onlyAssigned && modalData.trainerId ? modalData.trainerId : undefined);
  useEffect(() => {
    if (open) {
      setOnlyAssignedMembers(false);
      setMemberListOpen(false);
      setExpandedMemberIds(new Set());
    }
  }, [open]);
  // 회원 목록 ㄱㄴㄷ → 영어 → 숫자 → 특문 정렬 (판매등록 수강권 패널·담당강사 선택과 동일 규칙)
  const memberCharOrder = c => {
    if (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(c)) return 0;
    if (/[a-zA-Z]/.test(c)) return 1;
    if (/[0-9]/.test(c)) return 2;
    return 3;
  };
  const sortedSearchResults = useMemo(
    () =>
      [...(searchResults || [])].sort((a, b) => {
        const orderDiff = memberCharOrder((a.name || '')[0] || '') - memberCharOrder((b.name || '')[0] || '');
        if (orderDiff !== 0) return orderDiff;
        return (a.name || '').localeCompare(b.name || '', 'ko');
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults],
  );
  // 회원 목록 팝업 상단에 "담당 회원만 보기" 스위치를 얹는다. mousedown 을 막아 입력창 포커스(=팝업)를 유지한다.
  const MemberListPaper = useCallback(
    ({ children, ...paperProps }) => (
      <Paper {...paperProps}>
        <Box
          onMouseDown={e => e.preventDefault()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '12px',
            py: '4px',
            borderBottom: '1px solid #eee',
            fontSize: '12px',
            color: modalData.trainerId ? '#333' : '#aaa',
          }}>
          <span>담당 회원만 보기{modalData.trainerId ? '' : ' (강사를 먼저 선택하세요)'}</span>
          <Switch
            size="small"
            checked={onlyAssignedMembers}
            disabled={!modalData.trainerId}
            onChange={e => {
              const next = e.target.checked;
              setOnlyAssignedMembers(next);
              loadMembers(autoInputValue, next);
            }}
          />
        </Box>
        {children}
      </Paper>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onlyAssignedMembers, modalData.trainerId, autoInputValue],
  );
  const [isSaving, setIsSaving] = useState(false);

  // 비회원 상담 → 잠재회원 등록 확인 플로우
  const [pendingConsultation, setPendingConsultation] = useState(null);
  const [temporaryConfirmOpen, setTemporaryConfirmOpen] = useState(false);
  const [duplicateUsers, setDuplicateUsers] = useState([]);
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
  const [isRegisteringTemporary, setIsRegisteringTemporary] = useState(false);

  // 잠재회원으로 등록한 경우에만 상담 저장 뒤 판매등록을 이어서 물어본다.
  const pendingSaleTargetRef = useRef(null);
  const [saleConfirmOpen, setSaleConfirmOpen] = useState(false);
  const [saleTarget, setSaleTarget] = useState(null);

  // 상담 저장 성공 후 — 잠재회원을 새로 만든 흐름이면 모달을 닫지 않고 판매등록 여부를 묻는다.
  const handleConsultationSaved = () => {
    if (pendingSaleTargetRef.current) {
      setSaleConfirmOpen(true);
      return;
    }
    handleCancel();
  };

  const handleSkipSale = () => {
    pendingSaleTargetRef.current = null;
    setSaleConfirmOpen(false);
    handleCancel();
  };

  const handleConfirmSale = () => {
    setSaleTarget(pendingSaleTargetRef.current);
    pendingSaleTargetRef.current = null;
    setSaleConfirmOpen(false);
  };

  // 판매등록을 마치거나 중단하면 상담 모달까지 함께 닫는다.
  // 판매로 잠재회원이 회원으로 바뀌면 상담 카드의 대상 표시가 달라진다.
  // 하드 이동을 없앴으므로 여기서 직접 목록을 다시 읽어야 한다.
  const handleSaleModalClose = () => {
    setSaleTarget(null);
    queryClient.invalidateQueries({ queryKey: ['consultationList'] });
    queryClient.invalidateQueries({ queryKey: ['privateLessons'] });
    onRefresh?.();
    handleCancel();
  };

  // 상담 등록 mutation
  const createConsultationMutation = useMutation({
    mutationFn: ({ data }) => withOverlapConfirm(force => createConsultation(data, user?.token, force)),
    onSuccess: () => {
      onConflict?.(null);
      toast.success('상담이 등록되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['consultationList'] });
      onRefresh?.();
      handleConsultationSaved();
    },
    onError: error => {
      // 저장이 실패하면 판매등록 대기 대상도 함께 버린다.
      // 대상이 있었다면 잠재회원은 이미 만들어진 뒤라 그 사실을 알려야 한다.
      const temporaryCreated = !!pendingSaleTargetRef.current;
      pendingSaleTargetRef.current = null;
      // 겹침 확인창에서 취소했으면 에러 안내 없이 모달만 열어 둔다
      if (!error?.silent) {
        const conflict = error?.response?.data?.conflict;
        // 충돌이면 모달을 열어 둔 채 기억하고, 닫을 때 부딪힌 카드로 이동한다
        onConflict?.(conflict);
        toast.error(
          (error?.response?.data?.message || '상담 등록에 실패했습니다.') +
            (conflict?.id ? ' (닫으면 겹친 일정으로 이동합니다)' : ''),
        );
      }
      if (temporaryCreated) {
        toast.warning('잠재회원은 등록되었습니다. 잠재회원 관리에서 확인해주세요.');
      }
    },
  });

  // 상담 수정 mutation
  const updateConsultationMutation = useMutation({
    mutationFn: ({ consultationId, data }) =>
      withOverlapConfirm(force => updateConsultation(consultationId, data, user?.token, force)),
    onSuccess: () => {
      onConflict?.(null);
      toast.success('상담이 수정되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['consultationList'] });
      queryClient.invalidateQueries({ queryKey: ['privateLessons'] });
      onRefresh?.();
      handleConsultationSaved();
    },
    onError: error => {
      // 저장이 실패하면 판매등록 대기 대상도 함께 버린다.
      // 대상이 있었다면 잠재회원은 이미 만들어진 뒤라 그 사실을 알려야 한다.
      const temporaryCreated = !!pendingSaleTargetRef.current;
      pendingSaleTargetRef.current = null;
      // 겹침 확인창에서 취소했으면 에러 안내 없이 모달만 열어 둔다
      if (!error?.silent) {
        const conflict = error?.response?.data?.conflict;
        // 충돌이면 모달을 열어 둔 채 기억하고, 닫을 때 부딪힌 카드로 이동한다
        onConflict?.(conflict);
        toast.error(
          (error?.response?.data?.message || '상담 수정에 실패했습니다.') +
            (conflict?.id ? ' (닫으면 겹친 일정으로 이동합니다)' : ''),
        );
      }
      if (temporaryCreated) {
        toast.warning('잠재회원은 등록되었습니다. 잠재회원 관리에서 확인해주세요.');
      }
    },
  });

  // 상담 삭제 mutation
  const deleteConsultationMutation = useMutation({
    mutationFn: consultationId => deleteConsultation(consultationId, user?.token),
    onSuccess: () => {
      toast.success('상담이 삭제되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['consultationList'] });
      queryClient.invalidateQueries({ queryKey: ['privateLessons'] });
      onRefresh?.();
      handleCancel();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || '상담 삭제에 실패했습니다.');
    },
  });

  // 상담 삭제 핸들러
  const handleDeleteConsultation = () => {
    if (!modalData.consultationId) {
      toast.error('삭제할 상담 정보가 없습니다.');
      return;
    }
    if (window.confirm('상담을 삭제하시겠습니까?')) {
      deleteConsultationMutation.mutate(modalData.consultationId);
    }
  };

  // 기타 일정 저장 payload — 담당자는 임직원 ID(강사 컬럼 자리)로 보낸다.
  const buildBranchScheduleData = () => {
    const startHour = String(modalData.startHour ?? 0).padStart(2, '0');
    const startMinute = String(modalData.startMinute ?? 0).padStart(2, '0');
    const endHour = String(modalData.endHour ?? 0).padStart(2, '0');
    const endMinute = String(modalData.endMinute ?? 0).padStart(2, '0');

    return {
      branchId: user?.branch?.branchId,
      employeeId: modalData.trainerId,
      employeeName: modalData.trainerName,
      title: (modalData.etcTitle || '').trim(),
      scheduleDate: dayjs(modalData.registrationStartDate).format('YYYY-MM-DD'),
      startTime: `${startHour}:${startMinute}`,
      endTime: `${endHour}:${endMinute}`,
      content: modalData.memo || null,
    };
  };

  const invalidateScheduleQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['privateLessons'] });
    onRefresh?.();
  };

  // 기타 일정 등록 mutation
  const createBranchScheduleMutation = useMutation({
    mutationFn: data => createBranchSchedule(data, user?.token),
    onSuccess: () => {
      toast.success('기타 일정이 등록되었습니다.');
      invalidateScheduleQueries();
      handleCancel();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || '기타 일정 등록에 실패했습니다.');
    },
  });

  // 기타 일정 수정 mutation
  const updateBranchScheduleMutation = useMutation({
    mutationFn: ({ branchScheduleId, data }) => updateBranchSchedule(branchScheduleId, data, user?.token),
    onSuccess: () => {
      toast.success('기타 일정이 수정되었습니다.');
      invalidateScheduleQueries();
      handleCancel();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || '기타 일정 수정에 실패했습니다.');
    },
  });

  // 기타 일정 삭제 mutation
  const deleteBranchScheduleMutation = useMutation({
    mutationFn: branchScheduleId => deleteBranchSchedule(branchScheduleId, user?.token),
    onSuccess: () => {
      toast.success('기타 일정이 삭제되었습니다.');
      invalidateScheduleQueries();
      handleCancel();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || '기타 일정 삭제에 실패했습니다.');
    },
  });

  const handleDeleteBranchSchedule = () => {
    if (!modalData.branchScheduleId) {
      toast.error('삭제할 기타 일정 정보가 없습니다.');
      return;
    }
    if (window.confirm('기타 일정을 삭제하시겠습니까?')) {
      deleteBranchScheduleMutation.mutate(modalData.branchScheduleId);
    }
  };

  const isBranchSchedulePending =
    createBranchScheduleMutation.isPending || updateBranchScheduleMutation.isPending;

  const {
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      membersMaxCount: 0,
      memberId: modalData?.memberId || null,
      // select value는 optionKey(회원 티켓 단위 유일 키). 회원티켓ID 우선, 없으면 상품ID로 폴백
      ticketId:
        modalData?.memberCourseTicketId ??
        modalData?.memberTrialTicketId ??
        modalData?.tickets?.[0] ??
        modalData?.trialTickets?.[0] ??
        -1,
      exerciseRoomId: modalData?.exerciseRoomId || -1,
    },
  });
  useDirtyTracker(isDirty);

  // 바깥 클릭·Esc 로 닫기 — 사용자가 모달을 처음 만지는 순간(마우스·키보드)의 상태를 기준으로 삼는다.
  // 열린 뒤 늦게 도착한 자동 채움(강의실·상담 상세 등)은 사용자 입력이 아니므로 변경으로 보지 않는다.
  // 기준 이후 바뀐 게 있으면 작성 내용이 사라진다고 한 번 묻고, 아니면 바로 닫는다.
  const touchedRef = useRef(false);
  const baselineRef = useRef('');
  const [discardAsk, setDiscardAsk] = useState(false);
  // 듀엣 — 묶음 결제로 같이 산 상대가 있으면 자동으로 넣지 않고 같이 예약할지 묻는다(11/3)
  const [duetPartnerAsk, setDuetPartnerAsk] = useState(null); // { memberId, memberName }
  // 같은 회원·같은 듀엣 상품으로는 한 번만 묻는다 — 회원을 고를 때 물었으면 수강권을 고를 때 다시 묻지 않는다
  const duetAskedRef = useRef(new Set());
  const askDuetPartner = (courseTicketId, memberId) => {
    const key = `${memberId}:${courseTicketId}`;
    if (duetAskedRef.current.has(key)) return;
    duetAskedRef.current.add(key);
    getDuetPartners(courseTicketId, memberId, user?.token)
      .then(partners => {
        const partner = (partners || []).find(p => !selectedMembers.some(member => member.id === p.memberId));
        if (partner) setDuetPartnerAsk(partner);
      })
      // 상대 묻기는 편의 기능이라 실패해도 등록은 그대로 진행한다
      .catch(() => {});
  };
  // 회원을 고른 뒤 그 회원의 듀엣권이 하나뿐이면 수강권을 고르기 전에 바로 묻는다(여러 개면 수강권을 고를 때 묻는다)
  const duetAskTicketsRef = useRef(null);
  useEffect(() => {
    if (!open) {
      duetAskedRef.current = new Set();
      duetAskTicketsRef.current = null;
      return;
    }
    if (duetAskTicketsRef.current === tickets) return;
    duetAskTicketsRef.current = tickets;
    if (modalData.lessonType !== 'DUET_LESSON' || isUpdate || selectedMembers.length !== 1 || !modalData.memberId) {
      return;
    }
    const duetTickets = (tickets || []).filter(ticket => ticket.ticketType !== 'TRIAL_TICKET');
    if (duetTickets.length === 1 && duetTickets[0].courseTicketId != null) {
      askDuetPartner(duetTickets[0].courseTicketId, modalData.memberId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tickets]);
  // 레슨구분을 바꾸거나(듀엣 → OT → 듀엣) 회원을 모두 빼면 새로 고르는 것이라 다시 묻는다.
  // 예전엔 창을 닫을 때만 기록을 지워, 같은 회원을 다시 골라도 확인 창이 안 떴다(11/3 운영 제보).
  const noMemberSelected = selectedMembers.length === 0;
  useEffect(() => {
    duetAskedRef.current = new Set();
  }, [modalData.lessonType, noMemberSelected]);

  // 수업 날짜가 수강권 이용기간 밖이면 고르는 순간·날짜를 바꾸는 순간 알린다(저장 때 서버 406 으로 알던 것을 앞당김)
  const [periodAlert, setPeriodAlert] = useState(null); // { info, targetDate, actionLabel }

  // 수업 시간 맞추기 버튼 — '시작 기준'은 시작에 수업 시간을 더해 종료를, '종료 기준'은 종료에서 빼서 시작을 다시 정한다.
  // 수업 시간은 스케줄 설정의 구분별 기본 시간(개인·듀엣·OT·상담·기타). 설정을 못 읽으면 버튼을 숨긴다.
  const defaultLength = (() => {
    const defaults = getCreateDefaults?.(currentLessonType, 0, 0) || {};
    return defaults.endHour != null ? toMinutes(defaults.endHour, defaults.endMinute) : null;
  })();
  const changeTime = (field, value) => setModalData(prev => ({ ...prev, [field]: value }));
  const alignTime = base =>
    setModalData(prev => {
      if (base === 'START') {
        const end = toMinutes(prev.startHour, prev.startMinute) + defaultLength;
        if (end > LAST_SELECTABLE_MINUTES) {
          toast.error('종료 시간이 하루를 넘어 맞출 수 없습니다');
          return prev;
        }
        return { ...prev, endHour: Math.floor(end / 60), endMinute: end % 60 };
      }
      const begin = toMinutes(prev.endHour, prev.endMinute) - defaultLength;
      if (begin < 0) {
        toast.error('시작 시간이 0시보다 앞서 맞출 수 없습니다');
        return prev;
      }
      return { ...prev, startHour: Math.floor(begin / 60), startMinute: begin % 60 };
    });
  // 시간 칸 오른쪽 끝에 붙는 보조 동작 — 제목 한 줄 + 두 칸짜리 세그먼트 버튼. 입력 칸 높이(40px)에 맞춘다.
  const alignSegmentSx = {
    minWidth: 0,
    height: '24px',
    px: '10px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1,
    color: '#4A5563',
    borderRadius: 0,
    '&:hover': { backgroundColor: '#E3F2F3', color: '#0F7F85' },
  };
  const alignToggle = defaultLength ? (
    <Box sx={{ ml: 'auto', pl: '12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
      <Typography sx={{ fontSize: '11px', color: '#8A93A0', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
        기준 시간 맞추기 · {defaultLength}분
      </Typography>
      <Box
        sx={{
          display: 'flex',
          border: '1px solid #C3CBD3',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          whiteSpace: 'nowrap',
        }}>
        <Button disableElevation sx={alignSegmentSx} onClick={() => alignTime('START')}>
          시작 기준
        </Button>
        <Box sx={{ width: '1px', backgroundColor: '#C3CBD3' }} />
        <Button disableElevation sx={alignSegmentSx} onClick={() => alignTime('END')}>
          종료 기준
        </Button>
      </Box>
    </Box>
  ) : null;
  const createClassDate = modalData?.registrationStartDate
    ? dayjs(modalData.registrationStartDate).format('YYYY-MM-DD')
    : null;
  const ticketPeriodInfo = (ticket, member) => ({
    memberId: member?.id ?? ticket.memberId,
    memberName: member?.name ?? ticket.memberName,
    ticketName: ticket._name,
    startDate: ticket.periodStart ?? ticket.issueDate,
    expireDate: ticket.expireDate,
    memberCourseTicketId: ticket.memberCourseTicketId,
  });
  const alertIfOutOfPeriod = (ticket, member, date, actionLabel) => {
    if (!ticket || !isOutOfTicketPeriod(ticket.periodStart ?? ticket.issueDate, ticket.expireDate, date)) return false;
    setPeriodAlert({ info: ticketPeriodInfo(ticket, member), targetDate: date, actionLabel });
    return true;
  };
  const formSnapshot = JSON.stringify({ modalData, members: selectedMembers.map(m => m.id) });
  useEffect(() => {
    if (open) touchedRef.current = false;
  }, [open]);
  const markTouched = () => {
    if (touchedRef.current) return;
    touchedRef.current = true;
    baselineRef.current = formSnapshot;
  };
  const handleOutsideClose = (event, reason) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') return;
    if (touchedRef.current && formSnapshot !== baselineRef.current) {
      setDiscardAsk(true);
      return;
    }
    handleCancel();
  };

  // modalData가 변경될 때 form 값 업데이트
  React.useEffect(() => {
    if (open) {
      setValue('memberId', modalData?.memberId || null);
      setValue(
        'ticketId',
        modalData?.memberCourseTicketId ??
          modalData?.memberTrialTicketId ??
          modalData?.tickets?.[0] ??
          modalData?.trialTickets?.[0] ??
          -1,
      );
      setValue('exerciseRoomId', modalData?.exerciseRoomId || -1);
    }
  }, [open, modalData, setValue]);

  // 고를 수 있는 강의실이 하나뿐이면 굳이 고르게 하지 않고 자동으로 선택한다.
  // 모달을 열 때와 레슨구분을 바꿀 때 한 번씩만 채운다 — 매번 채우면 상담에서 '선택 안함'으로 되돌릴 수 없다.
  const autoRoomFilledForRef = useRef(null);
  useEffect(() => {
    if (!open) {
      autoRoomFilledForRef.current = null;
      return;
    }
    // 상담은 강의실이 선택 입력이라 기본값을 '선택 안함'으로 둔다 — 자동으로 채우지 않는다.
    if (isEtcType || isCounselType) return;
    if (autoRoomFilledForRef.current === currentLessonType) return;
    // 강의실 목록을 아직 못 받았으면 다음 렌더에 다시 시도한다.
    if (!selectableRooms?.length) return;
    autoRoomFilledForRef.current = currentLessonType;
    if (modalData?.exerciseRoomId && modalData.exerciseRoomId !== -1) return;
    const soleRoomId = pickAutoRoomId(selectableRooms, currentLessonType);
    if (soleRoomId == null) return;
    setModalData(prev => ({ ...prev, exerciseRoomId: soleRoomId }));
    setValue('exerciseRoomId', soleRoomId);
  }, [open, isEtcType, isCounselType, currentLessonType, selectableRooms, modalData?.exerciseRoomId, setModalData, setValue]);

  // 상담 저장 payload — onSubmit 과 [잠재회원 등록] 버튼이 함께 쓴다.
  const buildConsultationData = () => {
    const consultType = modalData.consultType || 'MEMBER';

    if (consultType !== 'NON_MEMBER' && !selectedMembers[0]?.id) {
      toast.error(consultType === 'TEMPORARY' ? '잠재회원을 선택해주세요.' : '회원을 선택해주세요.');
      return null;
    }

    if (consultType === 'NON_MEMBER' && !modalData.nonMemberName) {
      toast.error('이름을 입력해주세요.');
      return null;
    }

    // 상담 날짜 (LocalDate 형식: YYYY-MM-DD)
    const consultDate = dayjs(modalData.date || modalData.registrationStartDate).format('YYYY-MM-DD');

    // 상담 시간 (LocalTime 형식: HH:mm)
    const startHour = String(modalData.startHour || 0).padStart(2, '0');
    const startMinute = String(modalData.startMinute || 0).padStart(2, '0');
    const endHour = String(modalData.endHour || 0).padStart(2, '0');
    const endMinute = String(modalData.endMinute || 0).padStart(2, '0');

    const consultationData = {
      branchId: user?.branch?.branchId,
      consultType: consultType,
      title: modalData.consultTitle || `${selectedMembers[0]?.name || modalData.nonMemberName || ''} 상담`,
      consultDate: consultDate,
      consultStartTime: `${startHour}:${startMinute}`,
      consultEndTime: `${endHour}:${endMinute}`,
      consultMethod: modalData.consultMethod || null,
      consultPurpose: modalData.consultPurpose || null,
      consultResult: modalData.consultResult || 'SCHEDULED',
      // PUT 이 전체 교체라 방문경로·운동목적을 빠뜨리면 지워진다.
      visitPath: modalData.visitPath || null,
      visitPathEtc: modalData.visitPath === VISIT_PATH_OTHER ? modalData.visitPathEtc || null : null,
      exercisePurposes: modalData.exercisePurposes || [],
      managerId: modalData.trainerId ? Number(modalData.trainerId) : null,
      managerName: modalData.trainerName || '',
      content: modalData.memo || '',
      // 상담 강의실은 선택 입력이라 안 골랐으면 비워서 보낸다.
      exerciseRoomId:
        modalData.exerciseRoomId && modalData.exerciseRoomId !== -1 ? modalData.exerciseRoomId : null,
    };

    if (consultType === 'MEMBER') {
      consultationData.memberId = selectedMembers[0]?.id;
    } else if (consultType === 'TEMPORARY') {
      consultationData.temporaryUserId = selectedMembers[0]?.id;
    } else if (consultType === 'NON_MEMBER') {
      // 전화번호는 필수로 두되, 모르는 경우가 있어 비어 있으면 기본 번호로 채운다.
      const phone = modalData.nonMemberPhone || DEFAULT_NON_MEMBER_PHONE;
      consultationData.name = modalData.nonMemberName;
      consultationData.phone = phone;
      if (!modalData.nonMemberPhone) {
        setModalData(prev => ({ ...prev, nonMemberPhone: phone }));
      }
    }

    return consultationData;
  };

  const saveConsultation = consultationData => {
    if (isUpdate && modalData.consultationId) {
      updateConsultationMutation.mutate({
        consultationId: modalData.consultationId,
        data: consultationData,
      });
    } else {
      createConsultationMutation.mutate({ data: consultationData });
    }
  };

  // 수정 화면의 [잠재회원 등록] 버튼 — 신규 등록과 같은 확인 플로우를 탄다.
  const handleOpenTemporaryConfirm = () => {
    const consultationData = buildConsultationData();
    if (!consultationData) {
      return;
    }
    setPendingConsultation(consultationData);
    setTemporaryConfirmOpen(true);
  };

  // 확인창에서 '아니오' — 잠재회원 없이 비회원 상담 그대로 저장한다.
  const handleSkipTemporary = () => {
    setTemporaryConfirmOpen(false);
    if (pendingConsultation) {
      saveConsultation(pendingConsultation);
      setPendingConsultation(null);
    }
  };

  // 확인창에서 '예' — 잠재회원 등록 화면과 같은 기준(이 지점 고객만)으로 같은 번호 고객을 먼저 확인한다.
  const handleConfirmTemporary = async () => {
    setTemporaryConfirmOpen(false);
    const phone = (pendingConsultation?.phone || '').replaceAll('-', '');

    try {
      const results = await searchUserByPhoneNumber(phone, user?.branch?.branchId, true, true);
      if (results && results.length > 0) {
        setDuplicateUsers(results);
        setDuplicateConfirmOpen(true);
        return;
      }
    } catch {
      // 조회가 죽으면 중복 게이트가 통째로 사라진다. 등록을 막지는 않되 그 사실은 알린다.
      toast.warning('같은 번호 고객 확인에 실패했습니다. 중복 여부를 확인하지 못한 채 등록됩니다.');
    }

    registerTemporaryAndSave();
  };

  // 잠재회원을 만들고, 그 잠재회원의 상담으로 저장한다.
  const registerTemporaryAndSave = async () => {
    if (!pendingConsultation) {
      return;
    }
    setDuplicateConfirmOpen(false);
    setIsRegisteringTemporary(true);

    try {
      const created = await createTemporaryUser(
        converterJsonToFormData({
          name: pendingConsultation.name,
          phoneNumber: pendingConsultation.phone,
          branchId: user?.branch?.branchId,
          // 판매등록(회원 전환)이 성별을 필수로 요구해 기본값을 남자로 채운다 (잠재회원 등록 화면과 동일)
          gender: 'MALE',
        }),
      );

      if (!created?.id) {
        toast.error('잠재회원 등록에 실패했습니다.');
        return;
      }

      pendingSaleTargetRef.current = {
        userInfo: { userId: created.id, userType: 'temporary' },
        userDetail: {
          memberId: created.id,
          name: `${pendingConsultation.name} (${pendingConsultation.phone})`,
          phoneNumber: pendingConsultation.phone,
        },
      };

      saveConsultation({
        ...pendingConsultation,
        consultType: 'TEMPORARY',
        temporaryUserId: created.id,
      });
      setPendingConsultation(null);
    } catch (error) {
      // 같은 지점에 같은 번호가 이미 있으면 서버가 막는다. 상담도 저장하지 않고 모달을 유지한다.
      toast.error(error?.response?.data?.message || '잠재회원 등록에 실패했습니다.');
    } finally {
      setIsRegisteringTemporary(false);
    }
  };

  // 기타 일정 저장 — 회원·수강권·강의실 검증을 타지 않으므로 별도 경로로 끝낸다.
  const submitBranchSchedule = () => {
    if (!modalData.trainerId) {
      toast.error('담당자를 선택해 주세요');
      return;
    }
    if (!(modalData.etcTitle || '').trim()) {
      toast.error('제목을 입력해 주세요');
      return;
    }
    const startTotal = Number(modalData.startHour || 0) * 60 + Number(modalData.startMinute || 0);
    const endTotal = Number(modalData.endHour || 0) * 60 + Number(modalData.endMinute || 0);
    if (startTotal >= endTotal) {
      toast.error('종료 시간은 시작 시간보다 늦어야 합니다');
      return;
    }

    const data = buildBranchScheduleData();
    if (isUpdate && modalData.branchScheduleId) {
      updateBranchScheduleMutation.mutate({ branchScheduleId: modalData.branchScheduleId, data });
    } else {
      createBranchScheduleMutation.mutate(data);
    }
  };

  const onSubmit = async () => {
    if (isEtcType) {
      submitBranchSchedule();
      return;
    }

    const values = getValues();
    let hasError = false;
    const isCounsel = modalData.lessonType === 'COUNSEL';

    // 강의실 필수 체크 (상담 타입 제외)
    if (!isCounsel && (!modalData?.exerciseRoomId || modalData?.exerciseRoomId === -1)) {
      setError('exerciseRoomId', {
        type: 'required',
        message: '강의실을 선택해주세요.',
      });
      toast.error('강의실을 선택해주세요.');
      hasError = true;
    }

    // 회원 필수 체크 (비회원 타입이 아닐 때만)
    const isNonMember = isCounsel && modalData.consultType === 'NON_MEMBER';
    if (!isNonMember && selectedMembers.length === 0) {
      setError('memberId', {
        type: 'required',
        message: '회원을 선택해주세요.',
      });
      toast.error('회원을 선택해주세요.');
      hasError = true;
    }

    // 담당강사 필수 체크 (주간 패널에서 강사 미지정으로 생성 진입한 경우)
    if (trainerSelectable && !isUpdate && !modalData.trainerId) {
      toast.error('담당강사를 선택해 주세요');
      hasError = true;
    }

    // 수강권 필수 체크 (개인레슨 또는 듀엣레슨인 경우, 상담·OT 제외 — OT 는 회원별 수강권을 자동 해석)
    if (
      !isCounsel &&
      !isOtType &&
      (courseClassType === 'PRIVATE_LESSON' || modalData.lessonType === 'DUET_LESSON') &&
      (!values.ticketId || values.ticketId === -1)
    ) {
      setError('ticketId', {
        type: 'required',
        message: '수강권을 선택해주세요.',
      });
      toast.error('수강권을 선택해주세요.');
      hasError = true;
    }

    // OT 인원 수 검증 — 입력 중에는 막지 않으므로 저장 시점에 확인한다
    if (isOtType) {
      const count = Number(modalData.membersMaxCount);
      if (!Number.isInteger(count) || count < 1) {
        toast.error('인원 수를 1명 이상으로 입력해 주세요');
        hasError = true;
      } else if (isUpdate && count < otMinCount) {
        toast.error(`현재 예약 인원(${otMinCount}명)보다 작게 줄일 수 없습니다`);
        hasError = true;
      } else if (!isUpdate && selectedMembers.length > count) {
        // 회원은 인원 수 이하 — 정원보다 적게 만들어도 된다(사용자 확정 2026-09-29)
        toast.error(`선택한 회원(${selectedMembers.length}명)이 인원 수(${count}명)보다 많습니다`);
        hasError = true;
      }
    }

    if (modalData.startHour != null && modalData.endHour != null) {
      const startH = Number(modalData.startHour);
      const startM = Number(modalData.startMinute || 0);
      const endH = Number(modalData.endHour);
      const endM = Number(modalData.endMinute || 0);
      if (startH * 60 + startM >= endH * 60 + endM) {
        toast.error(
          isCounsel ? '상담 종료 시간은 시작 시간보다 늦어야 합니다' : '수업 종료 시간은 시작 시간보다 늦어야 합니다',
        );
        hasError = true;
      }
    }

    if (hasError) return;

    // 상담 타입일 때는 상담 API 호출
    if (isCounsel) {
      const consultationData = buildConsultationData();
      if (!consultationData) {
        return;
      }

      // 비회원을 새로 등록하는 경우에만 잠재회원 전환을 물어본다. 수정은 푸터 버튼으로 따로 태운다.
      if (consultationData.consultType === 'NON_MEMBER' && !isUpdate) {
        setPendingConsultation(consultationData);
        setTemporaryConfirmOpen(true);
        return;
      }

      saveConsultation(consultationData);
      return;
    }

    // 기존 레슨 저장
    setIsSaving(true);
    await handleSaveEvent();
    setIsSaving(false);
  };

  // OT 는 담당강사·강의실·인원 수·차감 횟수를 2열 그리드에 두므로 행 간격을 그리드가 맡는다
  const fieldRowMb = isOtType ? 0 : '10px';

  // 담당강사
  const trainerField = (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: fieldRowMb }}>
      <FormLabel>{t('담당강사')}</FormLabel>
      {trainerSelectable && !isUpdate ? (
        <Box sx={{ flex: 1, display: 'flex' }}>
          <TrainerSelectField
            trainerId={modalData.trainerId}
            trainerOptions={trainerOptions}
            onChange={(id, name) => setModalData(prev => ({ ...prev, trainerId: id, trainerName: name }))}
          />
        </Box>
      ) : (
        <TextField
          size="small"
          disabled
          value={modalData.trainerName}
          sx={{ flex: 1 }}
          InputProps={{
            sx: {
              backgroundColor: '#f5f5f5',
            },
          }}
        />
      )}
    </Box>
  );

  // 인원 수 최소값 — 등록은 선택 회원 수, 수정은 현재 예약 인원(취소 제외)
  const otReservedCount =
    (Number(modalData.reservedMemberCount) || 0) +
    (Number(modalData.attendedMemberCount) || 0) +
    (Number(modalData.absentedMemberCount) || 0);
  const otMinCount = Math.max(1, isUpdate ? otReservedCount : selectedMembers.length);

  // OT 인원 수(정원). 회원은 인원 수까지 선택하고 전원 생성과 동시에 예약된다.
  const headcountField = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <FormLabel>
        {t('인원 수')}
        <RequiredMark>*</RequiredMark>
      </FormLabel>
      <CountStepper
        label="인원 수"
        unit="명"
        min={otMinCount}
        value={modalData.membersMaxCount ?? 1}
        // 타이핑 중 중간값(빈 값, '12' 입력 중의 '1')은 막지 않고 저장 시 검증한다.
        onChange={next => setModalData(prev => ({ ...prev, membersMaxCount: next }))}
      />
    </Box>
  );

  // 강의실
  const roomField = (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: fieldRowMb }}>
      <FormLabel>
        {t('강의실')}
        <RequiredMark>*</RequiredMark>
      </FormLabel>
      <Controller
        name="exerciseRoomId"
        control={control}
        rules={{ required: modalData?.exerciseRoomId === -1 ? 'Class room id is required' : null }}
        render={({ field, fieldState }) => (
          <ErrorHighlightWrapper hasError={!!errors.exerciseRoomId} sx={{ flex: 1 }}>
            <FormControl size="small" sx={{ width: '100%' }} error={!!errors.exerciseRoomId}>
              <Select
                {...field}
                value={modalData?.exerciseRoomId}
                onChange={e => {
                  setModalData({
                    ...modalData,
                    exerciseRoomId: e.target.value,
                  });
                  field.onChange(e);
                  clearErrors('exerciseRoomId');
                }}>
                <MenuItem value={-1}>{t('강의실을 선택해 주세요')}</MenuItem>
                {selectableRooms?.map(room => (
                  <MenuItem key={room.branchExerciseRoomId} value={room.branchExerciseRoomId}>
                    {room.branchExerciseRoomName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </ErrorHighlightWrapper>
        )}
      />
    </Box>
  );

  // 수강권 차감 횟수 — 그룹레슨과 동일하게 예약 출결 시 수강권에서 차감되는 횟수. 기본 1. OT 는 기본 정보 2열 그리드에 둔다.
  const deductionField = (isOtType || courseClassType === 'PRIVATE_LESSON' || modalData.lessonType === 'DUET_LESSON') && (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: fieldRowMb }}>
      <FormLabel>
        {t('차감 횟수')}
        <RequiredMark>*</RequiredMark>
      </FormLabel>
      <CountStepper
        label="차감 횟수"
        unit="회"
        value={modalData.courseTicketDeductionCount ?? 1}
        onChange={next => setModalData(prev => ({ ...prev, courseTicketDeductionCount: next }))}
      />
    </Box>
  );

  // 수업 정보 필드(강의실·수강권·차감 횟수·메모). OT 는 기본 정보 안에, 그 외는 수업 정보 섹션에 그린다.
  const classInfoFields = (
    <>
      {!isOtType && roomField}

      {/* 수강권 선택 */}
      {!isOtType && (courseClassType === 'PRIVATE_LESSON' || modalData.lessonType === 'DUET_LESSON') && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
          <FormLabel>
            {t('수강권')}
            <RequiredMark>*</RequiredMark>
          </FormLabel>
          <Controller
            name="ticketId"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <ErrorHighlightWrapper hasError={!!errors.ticketId} sx={{ flex: 1 }}>
                <FormControl size="small" error={!!errors.ticketId} sx={{ width: '100%' }}>
                  <Select
                    {...field}
                    value={field.value || -1}
                    readOnly={isUpdate}
                    onClick={isUpdate ? notifyLockedOnUpdate : undefined}
                    sx={{ backgroundColor: isUpdate ? '#f5f5f5' : 'inherit' }}
                    onChange={e => {
                      // select value는 회원 티켓 단위 유일 키(optionKey) — 같은 상품 2개 구분용
                      const selectedKey = e.target.value;
                      const candidate = tickets?.find(ticket => ticket.optionKey === selectedKey);
                      // 수업 날짜가 이 수강권 기간 밖이면 고르지 않고 알린다
                      if (
                        !isUpdate &&
                        alertIfOutOfPeriod(
                          candidate,
                          // 수강권 목록은 마지막으로 고른 회원 기준(modalData.memberId)이다 — 듀엣이면 첫 회원이 아닐 수 있다
                          selectedMembers.find(member => member.id === modalData.memberId) || {
                            id: modalData.memberId,
                            name: modalData.memberName,
                          },
                          createClassDate,
                          '사용할 수 없습니다',
                        )
                      ) {
                        return;
                      }
                      field.onChange(selectedKey);

                      if (selectedKey === -1) {
                        setModalData(prevData => ({
                          ...prevData,
                          tickets: [],
                          trialTickets: [],
                          memberCourseTicketId: null,
                          memberTrialTicketId: null,
                        }));
                        return;
                      }

                      const picked = tickets?.find(ticket => ticket.optionKey === selectedKey);
                      const isTrial = picked?.ticketType === 'TRIAL_TICKET';

                      setModalData(prevData => ({
                        ...prevData,
                        // tickets/trialTickets는 상품ID(수업↔상품 연결용) — picked에서 명시적으로 추출
                        tickets: isTrial ? [] : picked?.courseTicketId != null ? [picked.courseTicketId] : [],
                        trialTickets:
                          isTrial && picked?.trialTicketId != null ? [picked.trialTicketId] : [],
                        // 어느 회원 티켓인지 식별 — 같은 상품 2개일 때 오차감 방지(둘 중 하나만 세팅)
                        memberCourseTicketId: isTrial ? null : picked?.memberCourseTicketId ?? null,
                        memberTrialTicketId: isTrial ? picked?.memberTrialTicketId ?? null : null,
                      }));
                      clearErrors('ticketId');

                      // 듀엣: 묶음 결제로 같이 산 상대가 있으면 같이 예약할지 묻는다(상대 강제 안 함, 11/3).
                      if (
                        modalData.lessonType === 'DUET_LESSON' &&
                        !isUpdate &&
                        !isTrial &&
                        picked?.courseTicketId != null &&
                        selectedMembers.length < 2
                      ) {
                        askDuetPartner(picked.courseTicketId, modalData.memberId);
                      }
                    }}>
                    <MenuItem value={-1}>수강권을 선택해 주세요</MenuItem>
                    {tickets?.map(ticket => (
                      <MenuItem key={`${ticket.ticketType}-${ticket.optionKey}`} value={ticket.optionKey}>
                        {ticket.ticketType === 'TRIAL_TICKET' ? '[일일권] ' : ''}
                        {ticket._name}
                        {ticket.remainingCount
                          ? ` (잔여 ${ticket.remainingCount}회)`
                          : ticket.usageCount
                            ? ` ${ticket.usageCount}회`
                            : ''}
                        {ticket?.usagePeriodDisplay ? ` ${ticket.usagePeriodDisplay}` : ''}
                        {ticket?.expireDate ? ` · ${expireLabel(ticket.expireDate)}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ErrorHighlightWrapper>
            )}
          />
        </Box>
      )}

      {!isOtType && deductionField}

      {/* 메모 */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <FormLabel alignTop>{t('메모')}</FormLabel>
        <StyledTextarea
          minRows={3}
          style={{ flex: 1, height: '70px', fontSize: '13px', width: 'auto' }}
          onChange={e => setModalData({ ...modalData, memo: e.target.value })}
          name="memo"
          value={modalData.memo}
          placeholder={t('메모를 입력해 주세요')}
        />
      </Box>
    </>
  );

  return (
    <>
      <Modal open={open} onClose={handleOutsideClose}>
        <ModalContainer onMouseDownCapture={markTouched} onKeyDownCapture={markTouched}>
          <ModalHeader>
            <CommonModalTitle>
              {isUpdate ? t('스케줄 수정') : t('스케줄 등록')}
            </CommonModalTitle>
            <ModalCloseIcon onClick={handleCancel} />
          </ModalHeader>
          {/* 레슨 타입 선택 (고정 영역) */}
          <LessonTypeBar>
            <LessonTypeBarLabel variant="label">{t('레슨구분')}</LessonTypeBarLabel>
            <LessonTypeChipGroup>
              {[
                { value: 'PRIVATE_LESSON', label: '개인레슨' },
                { value: 'DUET_LESSON', label: '듀엣레슨' },
                { value: 'OT', label: 'OT' },
                { value: 'COUNSEL', label: '상담' },
                { value: 'ETC', label: '기타' },
              ]
                // 수정 모드에서는 레슨구분을 바꿀 수 없다. 구분 변경은 수정이 아니라 새 스케줄 등록이다.
                // 수업 수정 진입 시 modalData에는 lessonType 없이 courseClassType만 담기므로 함께 본다.
                .filter(option => !isUpdate || option.value === currentLessonType)
                .map(option => {
                  const isActive = option.value === currentLessonType;
                  return (
                    <LessonTypeChip
                      key={option.value}
                      isActive={isActive}
                      onClick={
                        isUpdate
                          ? undefined
                          : () => {
                              // 레슨구분이 바뀌면 고를 수 있는 강의실 유형도 달라지므로 선택을 비운다.
                              // 기본값이 주어지면 종료 시각(시작 + 구분별 기본 수업 시간)과 구분별 기본 강의실로 채운다.
                              const defaults =
                                getCreateDefaults?.(option.value, modalData.startHour, modalData.startMinute) || {};
                              setModalData(prev => ({
                                ...prev,
                                lessonType: option.value,
                                consultType: 'MEMBER',
                                exerciseRoomId: -1,
                                // 인원 수·회원별 수강권 선택은 OT 전용이라 구분이 바뀌면 함께 비운다.
                                membersMaxCount: option.value === 'OT' ? 1 : 0,
                                otTicketSelections: {},
                                ...defaults,
                              }));
                              setValue('exerciseRoomId', defaults.exerciseRoomId ?? -1);
                              setSelectedMembers([]);
                              setSearchResults([]);
                              setAutoInputValue('');
                              setTickets([]);
                              setValue('memberId', null);
                            }
                      }>
                      <LessonTypeChipText isActive={isActive}>{option.label}</LessonTypeChipText>
                    </LessonTypeChip>
                  );
                })}
            </LessonTypeChipGroup>
          </LessonTypeBar>

          <ContentArea>
            {/* 상담·기타가 아닐 때: 개인레슨/듀엣레슨 UI (섹션 스타일) */}
            {!isCounselType && !isEtcType && (
              <ContentInner>
                {/* 기본 정보 섹션 */}
                <SectionCard>
                  <SectionHeader variant="label">기본 정보</SectionHeader>

                  {/* OT: 담당강사·강의실 / 인원 수·차감 횟수 2열 그리드, 그 아래 회원은 한 줄 전체 */}
                  {isOtType ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        columnGap: '24px',
                        rowGap: '10px',
                        alignItems: 'center',
                        pb: '12px',
                        mb: '12px',
                        borderBottom: '1px solid #e9ecef',
                      }}>
                      {trainerField}
                      {roomField}
                      {headcountField}
                      {deductionField}
                    </Box>
                  ) : (
                    trainerField
                  )}

                  {/* 회원 선택 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: selectedMembers.length > 0 ? '6px' : '10px' }}>
                    <FormLabel>
                      {t('회원')}
                      <RequiredMark>*</RequiredMark>
                      {isOtType && (
                        <span
                          style={{
                            marginLeft: '4px',
                            fontSize: '12px',
                            // 등록 시 선택 인원이 인원 수에 못 미치면 강조
                            color: !isUpdate && selectedMembers.length > otMaxMembers ? '#d32f2f' : '#999',
                            fontWeight: !isUpdate && selectedMembers.length > otMaxMembers ? 600 : 400,
                          }}>
                          {selectedMembers.length}/{otMaxMembers}
                        </span>
                      )}
                    </FormLabel>
                    {/* 수정 모드에서는 회원을 바꿀 수 없다. 회원 변경은 삭제 후 새로 등록해야 한다. */}
                    <Controller
                      name="memberId"
                      control={control}
                      rules={{ required: true }}
                      placeholder={t("Type the client's name")}
                      render={({ field, fieldState }) => (
                        <ErrorHighlightWrapper hasError={!!errors.memberId} sx={{ flex: 1 }}>
                          {/* Autocomplete는 getRootProps가 onClick을 덮어쓰므로 감싸는 FormControl에서 받는다. */}
                          <FormControl
                            sx={{ width: '100%' }}
                            error={!!errors.memberId}
                            onClick={isUpdate ? notifyLockedOnUpdate : undefined}>
                            <Autocomplete
                              size="small"
                              readOnly={isUpdate}
                              sx={{
                                width: '100%',
                                '& .MuiAutocomplete-popupIndicator': { transform: 'none' },
                              }}
                              componentsProps={{
                                popper: {
                                  placement: 'bottom-start',
                                  modifiers: [{ name: 'flip', enabled: false }],
                                },
                              }}
                              onChange={(e, v) => {
                                if (v) {
                                  const isAlreadyAdded = selectedMembers.some(member => member.id === v.id);
                                  const maxMembers =
                                    modalData.lessonType === 'DUET_LESSON' ? 2 : isOtType ? otMaxMembers : 1;

                                  if (isAlreadyAdded) {
                                    toast.error('이미 추가된 회원입니다');
                                    return;
                                  }

                                  if (selectedMembers.length >= maxMembers) {
                                    if (modalData.lessonType === 'DUET_LESSON') {
                                      toast.error('듀엣레슨은 2명까지 선택 가능합니다');
                                    } else if (isOtType) {
                                      toast.error(`OT 인원 수(${otMaxMembers}명)까지 선택 가능합니다`);
                                    } else {
                                      toast.error('개인레슨은 1명만 선택 가능합니다');
                                    }
                                    return;
                                  }

                                  const nameOnly = v.name;
                                  if (modalData.lessonType === 'DUET_LESSON' || isOtType) {
                                    setSelectedMembers(prev => [...prev, { id: v.id, name: nameOnly }]);
                                  } else {
                                    setSelectedMembers([{ id: v.id, name: nameOnly }]);
                                  }

                                  field.onChange(v.id);
                                  setValue('memberId', v.id);
                                  clearErrors('memberId');

                                  if (isUpdate) {
                                    setIsMemberChanged(true);
                                    setValue('ticketId', -1);
                                    setModalData(prev => ({
                                      ...prev,
                                      memberId: v.id,
                                      memberName: v.name,
                                      tickets: [],
                                    }));
                                  } else {
                                    setModalData(prev => ({
                                      ...prev,
                                      memberId: v.id,
                                      memberName: v.name,
                                    }));
                                  }

                                  setSearchResults([]);
                                  startTransition(() => {
                                    setValue('searchInput', '');
                                  });

                                  handleChangeMember(v.id);
                                } else {
                                  field.onChange(null);
                                  setValue('memberId', null);
                                  setModalData(prev => ({
                                    ...prev,
                                    memberId: '',
                                    memberName: '',
                                  }));
                                  setSearchResults([]);
                                  setTickets([]);
                                }
                              }}
                              onInputChange={(e, newInputValue, reason) => {
                                setAutoInputValue(reason === 'reset' ? '' : newInputValue);
                                if (!newInputValue || newInputValue.trim() === '') {
                                  setTickets([]);
                                  // 입력을 지운 경우는 아래 재조회로 전체 목록을 유지한다
                                  if (reason !== 'input') setSearchResults([]);
                                }
                                if (reason === 'input') {
                                  loadMembers(newInputValue);
                                  if (
                                    newInputValue !==
                                    (searchResults.find(option => option.id === field.value)?.name || '')
                                  ) {
                                    field.onChange(null);
                                    setValue('memberId', null);
                                    setModalData(prev => ({
                                      ...prev,
                                      memberId: '',
                                      memberName: '',
                                    }));
                                  }
                                }
                              }}
                              value={null}
                              inputValue={autoInputValue}
                              open={!isUpdate && memberListOpen}
                              openOnFocus
                              onOpen={() => {
                                setMemberListOpen(true);
                                loadMembers(autoInputValue);
                              }}
                              onClose={() => setMemberListOpen(false)}
                              PaperComponent={MemberListPaper}
                              disablePortal
                              options={sortedSearchResults}
                              groupBy={option => getChosung((option.name || '')[0] || '')}
                              renderGroup={params => (
                                <li key={params.key}>
                                  <Box sx={{ px: '14px', py: '4px', backgroundColor: '#f7f7f7', borderBottom: '1px solid #eee', position: 'sticky', top: 0, zIndex: 1 }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#999' }}>{params.group}</Typography>
                                  </Box>
                                  <ul style={{ padding: 0 }}>{params.children}</ul>
                                </li>
                              )}
                              getOptionLabel={() => ''}
                              noOptionsText={onlyAssignedMembers ? '담당 회원이 없습니다' : '검색결과가 없습니다'}
                              filterOptions={x => x}
                              renderOption={(props, option) => {
                                const classType =
                                  modalData.lessonType === 'DUET_LESSON' || isOtType ? modalData.lessonType : 'PRIVATE_LESSON';
                                const matched = (option.tickets || []).filter(t => t.classType === classType && t.status === 'ACTIVE');
                                const isSelected = selectedMembers.some(m => m.id === option.id);
                                const isExpanded = expandedMemberIds.has(option.id);
                                return (
                                  <li {...props} key={option.id} style={{ ...props.style, flexDirection: 'column', alignItems: 'flex-start', padding: '8px 16px', backgroundColor: isSelected ? '#E6F7F8' : undefined, borderLeft: isSelected ? '3px solid #4AB3BC' : '3px solid transparent' }}>
                                    <div>
                                      <span style={{ fontWeight: isSelected ? 700 : 500, color: isSelected ? '#0F7F85' : undefined }}>{option.name}</span>
                                      {option.phoneNumber && <span style={{ color: '#999', marginLeft: '6px' }}>({option.phoneNumber})</span>}
                                      {isSelected && <span style={{ color: '#4AB3BC', marginLeft: '8px', fontSize: '12px', fontWeight: 600 }}>선택됨</span>}
                                    </div>
                                    {matched.length > 0 ? (
                                      <>
                                        {(isExpanded ? matched : matched.slice(0, 1)).map((t, i) => (
                                          <div key={i} style={{ fontSize: '12px', color: '#4AB3BC', marginTop: '2px' }}>
                                            {t.name} · 잔여 {t.remaining}회{t.endDate ? ` · ${expireLabel(t.endDate)}` : ''}
                                          </div>
                                        ))}
                                        {matched.length > 1 && (
                                          // 펼치기는 회원 선택이 아니므로 클릭 전파를 막고, mousedown 도 막아 입력창 포커스(=팝업)를 유지한다
                                          <div
                                            onMouseDown={e => e.preventDefault()}
                                            onClick={e => {
                                              e.stopPropagation();
                                              toggleExpandedMember(option.id);
                                            }}
                                            style={{ fontSize: '11px', color: '#999', marginTop: '2px', cursor: 'pointer', textDecoration: 'underline' }}>
                                            {isExpanded ? '접기 ▴' : `외 ${matched.length - 1}개 펼치기 ▾`}
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div style={{ fontSize: '12px', color: '#ccc', marginTop: '2px' }}>보유 수강권 없음</div>
                                    )}
                                  </li>
                                );
                              }}
                              blurOnSelect={true}
                              clearOnBlur={true}
                              popupIcon={<SearchIcon sx={{ color: '#9a9a9a' }} />}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder={selectedMembers.length > 0 ? '' : '회원명 또는 전화번호로 검색'}
                                  InputProps={{
                                    ...params.InputProps,
                                    // OT 등록은 아래 회원별 수강권 목록에 이름이 있어 입력칸엔 칩을 두지 않는다
                                    startAdornment: isOtType && !isUpdate
                                      ? params.InputProps.startAdornment
                                      : selectedMemberAdornment(
                                          selectedMembers,
                                          isUpdate ? null : id => setSelectedMembers(prev => prev.filter(m => m.id !== id)),
                                          params.InputProps.startAdornment,
                                        ),
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '6px',
                                      backgroundColor: isUpdate ? '#f5f5f5' : 'inherit',
                                    },
                                    '& .MuiOutlinedInput-input': {
                                      fontSize: '13px',
                                    },
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                        </ErrorHighlightWrapper>
                      )}
                    />
                  </Box>

                  {/* OT 등록: 회원마다 이름 + 자기 OT 수강권/일일권 드롭다운 */}
                  {isOtType && !isUpdate && selectedMembers.length > 0 && (
                    <Box sx={{ ml: '80px', mb: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {selectedMembers.map(member => {
                        const list = otMemberTickets[member.id];
                        const selectedKey = modalData.otTicketSelections?.[member.id] ?? -1;
                        return (
                          <Box
                            key={member.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              p: '6px 6px 6px 12px',
                              border: '1px solid #E3E8EC',
                              borderRadius: '8px',
                              backgroundColor: '#F7FAFB',
                            }}>
                            <Typography
                              sx={{
                                width: '72px',
                                flexShrink: 0,
                                textAlign: 'center',
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#1F2A37',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                              {member.name}
                            </Typography>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {/* 개인레슨처럼 조회 중에도 드롭다운을 바로 보여 준다(목록은 응답 후 채워짐) */}
                              {otTicketErrors[member.id] ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Typography sx={{ fontSize: '13px', color: '#d32f2f' }}>수강권 조회 실패</Typography>
                                  <Button
                                    size="small"
                                    onClick={() => onRetryOtTickets?.(member.id)}
                                    sx={{ fontSize: '12px', p: '0 8px', minWidth: 0 }}>
                                    다시 시도
                                  </Button>
                                </Box>
                              ) : list !== undefined && list.length === 0 ? (
                                <Typography sx={{ fontSize: '13px', color: '#d32f2f' }}>OT 수강권 없음</Typography>
                              ) : (
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={(list || []).some(ticket => otTicketValue(ticket) === selectedKey) ? selectedKey : -1}
                                    onChange={e => {
                                      const candidate = (list || []).find(ticket => otTicketValue(ticket) === e.target.value);
                                      if (alertIfOutOfPeriod(candidate, member, createClassDate, '사용할 수 없습니다')) return;
                                      setModalData(prev => ({
                                        ...prev,
                                        otTicketSelections: { ...(prev.otTicketSelections || {}), [member.id]: e.target.value },
                                      }));
                                    }}
                                    sx={{ backgroundColor: '#fff', fontSize: '13px' }}>
                                    <MenuItem value={-1}>수강권을 선택해 주세요</MenuItem>
                                    {(list || []).map(ticket => (
                                      <MenuItem key={otTicketValue(ticket)} value={otTicketValue(ticket)}>
                                        {ticket.ticketType === 'TRIAL_TICKET' ? '[일일권] ' : ''}
                                        {ticket._name}
                                        {ticket.remainingCount
                                          ? ` (잔여 ${ticket.remainingCount}회)`
                                          : ticket.usageCount
                                            ? ` ${ticket.usageCount}회`
                                            : ''}
                                        {ticket?.usagePeriodDisplay ? ` ${ticket.usagePeriodDisplay}` : ''}
                                        {ticket?.expireDate ? ` · ${expireLabel(ticket.expireDate)}` : ''}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              aria-label={`${member.name} 선택 해제`}
                              onClick={() => {
                                setSelectedMembers(prev => prev.filter(m => m.id !== member.id));
                                // 해제한 회원의 수강권 선택도 함께 지운다
                                setModalData(prev => {
                                  const { [member.id]: _removed, ...rest } = prev.otTicketSelections || {};
                                  return { ...prev, otTicketSelections: rest };
                                });
                              }}
                              sx={{ p: '4px', color: '#8A94A0' }}>
                              <CloseIcon sx={{ fontSize: '16px' }} />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {isOtType && classInfoFields}
                </SectionCard>

                {/* 일정 정보 섹션 */}
                <SectionCard>
                  <SectionHeader variant="label">일정 정보</SectionHeader>

                  {/* 수업 날짜 — 수정에서만. 날짜를 옮기면 저장 때 확인 단계(출결·푸시)를 거친다 */}
                  {isUpdate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                      <FormLabel>{t('수업 날짜')}</FormLabel>
                      <CommonDatePicker
                        value={modalData.classDate}
                        onChange={(date, keyboardInput) => {
                          // 연도를 덜 입력한 중간값(0202-…)·일자를 덜 입력한 중간값(2026-10-2)은 반영하지 않는다
                          if (!date || Number.isNaN(date.getTime()) || date.getFullYear() < 1900) return;
                          if (isPartialDateInput(keyboardInput)) return;
                          const nextDate = dayjs(date).format('YYYY-MM-DD');
                          // 예약 수강권 기간 밖 날짜는 반영하지 않고 바로 알린다
                          const outTicket = (tickets || []).find(ticket =>
                            isOutOfTicketPeriod(ticket.periodStart, ticket.expireDate, nextDate),
                          );
                          if (outTicket && alertIfOutOfPeriod(outTicket, null, nextDate, '옮길 수 없습니다')) return;
                          setModalData(prev => ({ ...prev, classDate: nextDate }));
                        }}
                        sx={{ width: '170px' }}
                      />
                    </Box>
                  )}

                  {/* 수업 시간 */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormLabel>{t('수업 시간')}</FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: '70px', mr: '5px' }}>
                        <Select
                          value={modalData.startHour}
                          onChange={e => changeTime('startHour', parseInt(e.target.value, 10))}>
                          {hourOptions.map(hour => (
                            <MenuItem key={hour} value={hour}>
                              {hour.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography sx={{ mr: '5px' }}>:</Typography>
                      <FormControl size="small" sx={{ minWidth: '70px', mr: '10px' }}>
                        <Select
                          value={modalData.startMinute}
                          onChange={e => changeTime('startMinute', parseInt(e.target.value, 10))}>
                          {minuteOptions.map(minute => (
                            <MenuItem key={minute} value={minute}>
                              {minute.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography sx={{ mr: '10px', color: '#666' }}>~</Typography>
                      <FormControl size="small" sx={{ minWidth: '70px', mr: '5px' }}>
                        <Select
                          value={modalData.endHour}
                          onChange={e => changeTime('endHour', parseInt(e.target.value, 10))}>
                          {hourOptions.map(hour => (
                            <MenuItem key={hour} value={hour}>
                              {hour.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography sx={{ mr: '5px' }}>:</Typography>
                      <FormControl size="small" sx={{ minWidth: '70px' }}>
                        <Select
                          value={modalData.endMinute}
                          onChange={e => changeTime('endMinute', parseInt(e.target.value, 10))}>
                          {minuteOptions.map(minute => (
                            <MenuItem key={minute} value={minute}>
                              {minute.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {alignToggle}
                  </Box>
                </SectionCard>

                {/* 수업 정보 섹션 (OT 는 기본 정보에 합침) */}
                {!isOtType && (
                  <SectionCard>
                    <SectionHeader variant="label">수업 정보</SectionHeader>
                    {classInfoFields}
                  </SectionCard>
                )}
              </ContentInner>
            )}

            {/* 상담 타입일 때: 상담 전용 필드들 */}
            {isCounselType && (
              <ContentInner>
                {/* 기본 정보 섹션 */}
                <SectionCard>
                  <SectionHeader variant="label">기본 정보</SectionHeader>

                  {/* 상담 제목 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                    <FormLabel>{t('상담 제목')}</FormLabel>
                    <TextField
                      size="small"
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '4px',
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '13px',
                        },
                      }}
                      placeholder={t('상담 제목을 입력해 주세요')}
                      value={modalData.consultTitle || ''}
                      onChange={e => setModalData(prev => ({ ...prev, consultTitle: e.target.value }))}
                    />
                  </Box>

                  {/* 상담 대상 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                    <FormLabel>
                      {t('상담 대상')}
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    <RadioGroup
                      row
                      value={modalData.consultType || 'MEMBER'}
                      onChange={e => {
                        setModalData(prev => ({
                          ...prev,
                          consultType: e.target.value,
                          nonMemberName: '',
                          nonMemberPhone: '',
                        }));
                        setSelectedMembers([]);
                        setSearchResults([]);
                      }}
                      sx={{ gap: '8px' }}>
                      {CONSULT_TYPE_OPTIONS.map(option => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio size="small" sx={{ p: '4px' }} />}
                          label={<Typography sx={{ fontSize: '13px' }}>{t(option.label)}</Typography>}
                          sx={{ m: 0 }}
                        />
                      ))}
                    </RadioGroup>
                  </Box>

                  {/* 회원/잠재회원 검색 (비회원이 아닐 때) */}
                  {modalData.consultType !== 'NON_MEMBER' && (
                    <>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: selectedMembers.length > 0 ? '6px' : '10px' }}>
                        <FormLabel>
                          {modalData.consultType === 'TEMPORARY' ? t('잠재회원') : t('회원')}
                          <RequiredMark>*</RequiredMark>
                        </FormLabel>
                        <Controller
                          name="memberId"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <ErrorHighlightWrapper hasError={!!errors.memberId} sx={{ flex: 1 }}>
                              <FormControl size="small" sx={{ width: '100%' }} error={!!errors.memberId}>
                                <Autocomplete
                                  size="small"
                                  sx={{ width: '100%', '& .MuiAutocomplete-popupIndicator': { transform: 'none' } }}
                                  componentsProps={{
                                    popper: {
                                      placement: 'bottom-start',
                                      modifiers: [{ name: 'flip', enabled: false }],
                                    },
                                  }}
                                  onChange={(e, v) => {
                                    if (v) {
                                      const isAlreadyAdded = selectedMembers.some(member => member.id === v.id);
                                      if (isAlreadyAdded) {
                                        toast.error('이미 추가된 회원입니다');
                                        return;
                                      }
                                      if (selectedMembers.length >= 1) {
                                        toast.error('상담은 1명만 선택 가능합니다');
                                        return;
                                      }
                                      const nameOnly = v.name;
                                      setSelectedMembers([{ id: v.id, name: nameOnly }]);
                                      field.onChange(v.id);
                                      setValue('memberId', v.id);
                                      clearErrors('memberId');
                                      setModalData(prev => ({ ...prev, memberId: v.id, memberName: v.name }));
                                      setSearchResults([]);
                                      startTransition(() => {
                                        setValue('searchInput', '');
                                      });
                                    } else {
                                      field.onChange(null);
                                      setValue('memberId', null);
                                      setModalData(prev => ({ ...prev, memberId: '', memberName: '' }));
                                      setSearchResults([]);
                                    }
                                  }}
                                  onInputChange={(e, newInputValue, reason) => {
                                    setAutoInputValue(reason === 'reset' ? '' : newInputValue);
                                    setExpandedMemberIds(new Set());
                                    if (!newInputValue || newInputValue.trim() === '') {
                                      setSearchResults([]);
                                    }
                                    if (reason === 'input') {
                                      const memberType = modalData.consultType === 'TEMPORARY' ? 'TEMPORARY' : 'MEMBER';
                                      debouncedGetMembers(newInputValue, memberType);
                                      if (
                                        newInputValue !==
                                        (searchResults.find(option => option.id === field.value)?.name || '')
                                      ) {
                                        field.onChange(null);
                                        setValue('memberId', null);
                                        setModalData(prev => ({ ...prev, memberId: '', memberName: '' }));
                                      }
                                    }
                                  }}
                                  value={null}
                                  inputValue={autoInputValue}
                                  open={autoInputValue.trim().length > 0 && searchResults?.length > 0}
                                  disablePortal
                                  options={searchResults || []}
                                  getOptionLabel={() => ''}
                                  noOptionsText="검색결과가 없습니다"
                                  filterOptions={x => x}
                                  renderOption={(props, option) => {
                                    const activeTickets = (option.tickets || []).filter(t => t.status === 'ACTIVE');
                                    const isSelected = selectedMembers.some(m => m.id === option.id);
                                    const isExpanded = expandedMemberIds.has(option.id);
                                    return (
                                      <li {...props} key={option.id} style={{ ...props.style, flexDirection: 'column', alignItems: 'flex-start', padding: '8px 16px', backgroundColor: isSelected ? '#E6F7F8' : undefined, borderLeft: isSelected ? '3px solid #4AB3BC' : '3px solid transparent' }}>
                                        <div>
                                          <span style={{ fontWeight: isSelected ? 700 : 500, color: isSelected ? '#0F7F85' : undefined }}>{option.name}</span>
                                          {option.phoneNumber && <span style={{ color: '#999', marginLeft: '6px' }}>({option.phoneNumber})</span>}
                                          {isSelected && <span style={{ color: '#4AB3BC', marginLeft: '8px', fontSize: '12px', fontWeight: 600 }}>선택됨</span>}
                                        </div>
                                        {activeTickets.length > 0 ? (
                                          <>
                                            {(isExpanded ? activeTickets : activeTickets.slice(0, 1)).map((t, i) => (
                                              <div key={i} style={{ fontSize: '12px', color: '#4AB3BC', marginTop: '2px' }}>
                                                {t.name} · 잔여 {t.remaining}회{t.endDate ? ` · ${expireLabel(t.endDate)}` : ''}
                                              </div>
                                            ))}
                                            {activeTickets.length > 1 && (
                                              // 펼치기는 회원 선택이 아니므로 클릭 전파를 막고, mousedown 도 막아 입력창 포커스(=팝업)를 유지한다
                                              <div
                                                onMouseDown={e => e.preventDefault()}
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  toggleExpandedMember(option.id);
                                                }}
                                                style={{ fontSize: '11px', color: '#999', marginTop: '2px', cursor: 'pointer', textDecoration: 'underline' }}>
                                                {isExpanded ? '접기 ▴' : `외 ${activeTickets.length - 1}개 펼치기 ▾`}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div style={{ fontSize: '12px', color: '#ccc', marginTop: '2px' }}>보유 수강권 없음</div>
                                        )}
                                      </li>
                                    );
                                  }}
                                  blurOnSelect={true}
                                  clearOnBlur={true}
                                  openOnFocus={false}
                                  popupIcon={<SearchIcon sx={{ color: '#9a9a9a' }} />}
                                  renderInput={params => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      placeholder={selectedMembers.length > 0 ? '' : '회원명 또는 전화번호로 검색'}
                                      InputProps={{
                                        ...params.InputProps,
                                        startAdornment: selectedMemberAdornment(
                                          selectedMembers,
                                          id => setSelectedMembers(prev => prev.filter(m => m.id !== id)),
                                          params.InputProps.startAdornment,
                                        ),
                                      }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: '4px',
                                        },
                                        '& .MuiOutlinedInput-input': {
                                          fontSize: '13px',
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </FormControl>
                            </ErrorHighlightWrapper>
                          )}
                        />
                      </Box>

                    </>
                  )}

                  {/* 비회원일 경우: 이름, 전화번호 */}
                  {modalData.consultType === 'NON_MEMBER' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px', gap: '20px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <FormLabel>
                          {t('이름')}
                          <RequiredMark>*</RequiredMark>
                        </FormLabel>
                        <TextField
                          size="small"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '4px',
                            },
                            '& .MuiOutlinedInput-input': {
                              fontSize: '13px',
                            },
                          }}
                          placeholder={t('이름 입력')}
                          value={modalData.nonMemberName || ''}
                          onChange={e => setModalData(prev => ({ ...prev, nonMemberName: e.target.value }))}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <FormLabel>
                          {t('전화번호')}
                          <RequiredMark>*</RequiredMark>
                        </FormLabel>
                        <TextField
                          size="small"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '4px',
                            },
                            '& .MuiOutlinedInput-input': {
                              fontSize: '13px',
                            },
                          }}
                          placeholder="010-0000-0000"
                          value={modalData.nonMemberPhone || ''}
                          onChange={e =>
                            setModalData(prev => ({ ...prev, nonMemberPhone: formatPhoneNumber(e.target.value) }))
                          }
                        />
                      </Box>
                    </Box>
                  )}

                  {/* 담당자 */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormLabel>{t('담당자')}</FormLabel>
                    {trainerSelectable && !isUpdate ? (
                      <TrainerSelectField
                        trainerId={modalData.trainerId}
                        trainerOptions={trainerOptions}
                        onChange={(id, name) => setModalData(prev => ({ ...prev, trainerId: id, trainerName: name }))}
                      />
                    ) : (
                      <TextField
                        size="small"
                        disabled
                        value={modalData.trainerName || ''}
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '4px',
                          },
                          '& .MuiOutlinedInput-input': {
                            fontSize: '13px',
                          },
                        }}
                        InputProps={{
                          sx: {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      />
                    )}
                  </Box>
                </SectionCard>

                {/* 일정 정보 섹션 */}
                <SectionCard>
                  <SectionHeader variant="label">일정 정보</SectionHeader>

                  {/* 상담 날짜 — 수정에서만 */}
                  {isUpdate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                      <FormLabel>{t('상담 날짜')}</FormLabel>
                      <CommonDatePicker
                        value={modalData.date || modalData.registrationStartDate}
                        onChange={(date, keyboardInput) => {
                          if (!date || Number.isNaN(date.getTime()) || date.getFullYear() < 1900) return;
                          if (isPartialDateInput(keyboardInput)) return;
                          setModalData(prev => ({ ...prev, date: dayjs(date).format('YYYY-MM-DD') }));
                        }}
                        sx={{ width: '170px' }}
                      />
                    </Box>
                  )}

                  {/* 상담 시간 */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormLabel>{t('상담 시간')}</FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: '70px', mr: '5px' }}>
                        <Select
                          value={modalData.startHour}
                          onChange={e => changeTime('startHour', parseInt(e.target.value, 10))}>
                          {hourOptions.map(hour => (
                            <MenuItem key={hour} value={hour}>
                              {hour.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography sx={{ mr: '5px' }}>:</Typography>
                      <FormControl size="small" sx={{ minWidth: '70px', mr: '10px' }}>
                        <Select
                          value={modalData.startMinute}
                          onChange={e => changeTime('startMinute', parseInt(e.target.value, 10))}>
                          {minuteOptions.map(minute => (
                            <MenuItem key={minute} value={minute}>
                              {minute.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography sx={{ mr: '10px', color: '#666' }}>~</Typography>
                      <FormControl size="small" sx={{ minWidth: '70px', mr: '5px' }}>
                        <Select
                          value={modalData.endHour}
                          onChange={e => changeTime('endHour', parseInt(e.target.value, 10))}>
                          {hourOptions.map(hour => (
                            <MenuItem key={hour} value={hour}>
                              {hour.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography sx={{ mr: '5px' }}>:</Typography>
                      <FormControl size="small" sx={{ minWidth: '70px' }}>
                        <Select
                          value={modalData.endMinute}
                          onChange={e => changeTime('endMinute', parseInt(e.target.value, 10))}>
                          {minuteOptions.map(minute => (
                            <MenuItem key={minute} value={minute}>
                              {minute.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {alignToggle}
                  </Box>
                </SectionCard>

                {/* 상담 정보 섹션 */}
                <SectionCard>
                  <SectionHeader variant="label">상담 정보</SectionHeader>

                  {/* 상담 수단 & 상담 목적 (2열) */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormLabel>{t('상담 수단')}</FormLabel>
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <Select
                          displayEmpty
                          value={modalData.consultMethod || ''}
                          onChange={e => setModalData(prev => ({ ...prev, consultMethod: e.target.value }))}>
                          <MenuItem value="" disabled>
                            {t('선택')}
                          </MenuItem>
                          {CONSULT_METHOD_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {t(option.label)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormLabel>{t('상담 목적')}</FormLabel>
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <Select
                          displayEmpty
                          value={modalData.consultPurpose || ''}
                          onChange={e => setModalData(prev => ({ ...prev, consultPurpose: e.target.value }))}>
                          <MenuItem value="" disabled>
                            {t('선택')}
                          </MenuItem>
                          {CONSULT_PURPOSE_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {t(option.label)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* 강의실 — 선택 입력이라 안 골라도 저장된다 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: '10px' }}>
                    <FormLabel>{t('강의실')}</FormLabel>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        displayEmpty
                        value={
                          modalData.exerciseRoomId && modalData.exerciseRoomId !== -1
                            ? modalData.exerciseRoomId
                            : -1
                        }
                        onChange={e => setModalData(prev => ({ ...prev, exerciseRoomId: e.target.value }))}>
                        <MenuItem value={-1}>{t('선택 안함')}</MenuItem>
                        {selectableRooms?.map(room => (
                          <MenuItem key={room.branchExerciseRoomId} value={room.branchExerciseRoomId}>
                            {room.branchExerciseRoomName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* 상담 결과 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: '10px' }}>
                    <FormLabel>{t('상담 결과')}</FormLabel>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={modalData.consultResult || 'SCHEDULED'}
                        onChange={e => setModalData(prev => ({ ...prev, consultResult: e.target.value }))}>
                        {CONSULT_RESULT_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {t(option.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* 방문경로 — 기타면 직접 입력란이 열린다 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: '10px' }}>
                    <FormLabel>{t('방문경로')}</FormLabel>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        displayEmpty
                        value={modalData.visitPath || ''}
                        onChange={e => setModalData(prev => ({ ...prev, visitPath: e.target.value }))}>
                        <MenuItem value="">{t('선택 안함')}</MenuItem>
                        {visitPaths.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {t(option.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {modalData.visitPath === VISIT_PATH_OTHER && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: '10px' }}>
                      <FormLabel />
                      <TextField
                        size="small"
                        sx={{ flex: 1, '& .MuiOutlinedInput-input': { fontSize: '13px' } }}
                        placeholder={`방문경로 직접 입력 (${VISIT_PATH_ETC_MAX_LENGTH}자 이내)`}
                        value={modalData.visitPathEtc || ''}
                        onChange={e => setModalData(prev => ({ ...prev, visitPathEtc: e.target.value }))}
                        inputProps={{ maxLength: VISIT_PATH_ETC_MAX_LENGTH }}
                      />
                    </Box>
                  )}

                  {/* 운동목적 — 여러 개 고를 수 있다 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: '10px' }}>
                    <FormLabel alignTop>{t('운동목적')}</FormLabel>
                    <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {exercisePurposeOptions.map(option => {
                        const selected = (modalData.exercisePurposes || []).includes(option.value);
                        return (
                          <Box
                            key={option.value}
                            onClick={() =>
                              setModalData(prev => {
                                const current = prev.exercisePurposes || [];
                                return {
                                  ...prev,
                                  exercisePurposes: current.includes(option.value)
                                    ? current.filter(v => v !== option.value)
                                    : [...current, option.value],
                                };
                              })
                            }
                            sx={{
                              height: '34px',
                              padding: '0 14px',
                              borderRadius: '17px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              fontSize: '13px',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              backgroundColor: selected ? '#E6F2F3' : '#fff',
                              border: `1px solid ${selected ? '#0F7F85' : '#d9d9d9'}`,
                              color: selected ? '#0F7F85' : '#5f5f5f',
                              fontWeight: selected ? 600 : 400,
                            }}>
                            {t(option.label)}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* 상담 내용 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: '10px' }}>
                    <FormLabel alignTop>{t('상담내용')}</FormLabel>
                    <StyledTextarea
                      minRows={3}
                      style={{ flex: 1, height: '70px', fontSize: '13px', width: 'auto' }}
                      onChange={e => setModalData(prev => ({ ...prev, memo: e.target.value }))}
                      name="memo"
                      value={modalData.memo}
                      placeholder={t('상담 내용을 입력해 주세요')}
                    />
                  </Box>
                </SectionCard>
              </ContentInner>
            )}

            {/* 기타 타입일 때: 지점 내부 일정(정수기 점검·외출 등) */}
            {isEtcType && (
              <ContentInner>
                {/* 기본 정보 섹션 */}
                <SectionCard>
                  <SectionHeader variant="label">기본 정보</SectionHeader>

                  {/* 담당자 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                    <FormLabel>
                      {t('담당자')}
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    {trainerSelectable && !isUpdate ? (
                      <TrainerSelectField
                        trainerId={modalData.trainerId}
                        trainerOptions={trainerOptions}
                        onChange={(id, name) => setModalData(prev => ({ ...prev, trainerId: id, trainerName: name }))}
                      />
                    ) : (
                      <TextField
                        size="small"
                        disabled
                        value={modalData.trainerName || ''}
                        sx={{ flex: 1 }}
                        InputProps={{ sx: { backgroundColor: '#f5f5f5' } }}
                      />
                    )}
                  </Box>

                  {/* 제목 */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormLabel>
                      {t('제목')}
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    <TextField
                      size="small"
                      sx={{ flex: 1 }}
                      inputProps={{ maxLength: 100 }}
                      value={modalData.etcTitle || ''}
                      onChange={e => setModalData(prev => ({ ...prev, etcTitle: e.target.value }))}
                    />
                  </Box>
                </SectionCard>

                {/* 일정 정보 섹션 */}
                <SectionCard>
                  <SectionHeader variant="label">일정 정보</SectionHeader>

                  {/* 날짜 — 수정에서만 */}
                  {isUpdate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                      <FormLabel>{t('날짜')}</FormLabel>
                      <CommonDatePicker
                        value={modalData.registrationStartDate}
                        onChange={(date, keyboardInput) => {
                          if (!date || Number.isNaN(date.getTime()) || date.getFullYear() < 1900) return;
                          if (isPartialDateInput(keyboardInput)) return;
                          setModalData(prev => ({ ...prev, registrationStartDate: dayjs(date).format('YYYY-MM-DD') }));
                        }}
                        sx={{ width: '170px' }}
                      />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormLabel>{t('시간')}</FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: '70px', mr: '5px' }}>
                        <Select
                          value={modalData.startHour}
                          onChange={e => changeTime('startHour', parseInt(e.target.value, 10))}>
                          {hourOptions.map(hour => (
                            <MenuItem key={hour} value={hour}>
                              {hour.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography sx={{ mr: '5px' }}>:</Typography>
                      <FormControl size="small" sx={{ minWidth: '70px', mr: '10px' }}>
                        <Select
                          value={modalData.startMinute}
                          onChange={e => changeTime('startMinute', parseInt(e.target.value, 10))}>
                          {minuteOptions.map(minute => (
                            <MenuItem key={minute} value={minute}>
                              {minute.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography sx={{ mr: '10px', color: '#666' }}>~</Typography>
                      <FormControl size="small" sx={{ minWidth: '70px', mr: '5px' }}>
                        <Select
                          value={modalData.endHour}
                          onChange={e => changeTime('endHour', parseInt(e.target.value, 10))}>
                          {hourOptions.map(hour => (
                            <MenuItem key={hour} value={hour}>
                              {hour.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography sx={{ mr: '5px' }}>:</Typography>
                      <FormControl size="small" sx={{ minWidth: '70px' }}>
                        <Select
                          value={modalData.endMinute}
                          onChange={e => changeTime('endMinute', parseInt(e.target.value, 10))}>
                          {minuteOptions.map(minute => (
                            <MenuItem key={minute} value={minute}>
                              {minute.toString().padStart(2, '0')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {alignToggle}
                  </Box>

                  {/* 내용 */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: '10px' }}>
                    <FormLabel alignTop>{t('내용')}</FormLabel>
                    <StyledTextarea
                      minRows={3}
                      style={{ flex: 1, height: '70px', fontSize: '13px', width: 'auto' }}
                      maxLength={500}
                      onChange={e => setModalData(prev => ({ ...prev, memo: e.target.value }))}
                      name="memo"
                      value={modalData.memo || ''}
                      placeholder={t('내용을 입력해 주세요 (선택)')}
                    />
                  </Box>
                </SectionCard>
              </ContentInner>
            )}
          </ContentArea>

          {/* 푸터 (고정) */}
          <FooterBar>
            {/* 기타 수정 모드에서만 삭제 버튼 표시 */}
            {isEtcType && isUpdate && modalData.branchScheduleId ? (
              <CommonModalButton
                variant="delete"
                onClick={handleDeleteBranchSchedule}
                disabled={deleteBranchScheduleMutation.isPending}>
                {deleteBranchScheduleMutation.isPending ? t('삭제 중...') : t('삭제')}
              </CommonModalButton>
            ) : /* 상담 수정 모드에서만 삭제 버튼 표시 */
            isCounselType && isUpdate && modalData.consultationId ? (
              <CommonModalButton
                variant="delete"
                onClick={handleDeleteConsultation}
                disabled={deleteConsultationMutation.isPending}>
                {deleteConsultationMutation.isPending ? t('삭제 중...') : t('삭제')}
              </CommonModalButton>
            ) : (
              <Box />
            )}
            <FooterRightGroup>
              {/* 비회원으로 남아있는 상담만 잠재회원으로 옮길 수 있다. */}
              {isCounselType && isUpdate && modalData.consultType === 'NON_MEMBER' && (
                <CommonModalButton
                  variant="cancel"
                  onClick={handleOpenTemporaryConfirm}
                  disabled={isRegisteringTemporary}>
                  {t('잠재회원 등록')}
                </CommonModalButton>
              )}
              <CommonModalButton variant="cancel" onClick={handleCancel}>
                {t('Cancel')}
              </CommonModalButton>
              <CommonModalButton
                variant="submit"
                onClick={onSubmit}
                disabled={
                  isSaving ||
                  (isCounselType &&
                    (createConsultationMutation.isPending || updateConsultationMutation.isPending)) ||
                  (isEtcType && isBranchSchedulePending)
                }>
                {isSaving ||
                (isCounselType &&
                  (createConsultationMutation.isPending || updateConsultationMutation.isPending)) ||
                (isEtcType && isBranchSchedulePending)
                  ? t('저장 중...')
                  : t('Save')}
              </CommonModalButton>
            </FooterRightGroup>
          </FooterBar>
        </ModalContainer>
      </Modal>

      {/* 잠재회원 등록 여부 확인 */}
      <Dialog open={temporaryConfirmOpen} onClose={() => setTemporaryConfirmOpen(false)}>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 700 }}>{t('잠재회원 등록')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px' }}>
            {pendingConsultation?.name} ({pendingConsultation?.phone}) {t('님을 잠재회원으로 등록할까요?')}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#888', mt: '8px' }}>
            {t('등록하면 이 상담은 잠재회원 상담으로 저장됩니다.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <CommonModalButton variant="cancel" onClick={handleSkipTemporary}>
            {t('등록 안 함')}
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleConfirmTemporary}>
            {t('등록')}
          </CommonModalButton>
        </DialogActions>
      </Dialog>

      {/* 같은 번호 고객이 이미 있을 때 한 번 더 확인 */}
      <Dialog
        open={duplicateConfirmOpen}
        onClose={() => setDuplicateConfirmOpen(false)}
        fullWidth
        maxWidth="xs">
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 700 }}>{t('같은 번호 고객이 있습니다')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', mb: '10px' }}>
            {t('같은 전화번호로 등록된 고객이')} {duplicateUsers.length}
            {t('명 있습니다. 그래도 잠재회원으로 등록할까요?')}
          </Typography>
          {/* 더미번호(010-0000-0000)는 같은 번호 고객이 수십 명이라 목록이 화면을 넘친다. */}
          <Box sx={{ maxHeight: '160px', overflowY: 'auto' }}>
            {duplicateUsers.slice(0, DUPLICATE_PREVIEW_COUNT).map(duplicateUser => (
              <Typography
                key={duplicateUser.temporaryUserId ? `t-${duplicateUser.temporaryUserId}` : duplicateUser.userId}
                sx={{ fontSize: '13px', color: '#555' }}>
                {duplicateUser.name} ({duplicateUser.phoneNumber}){duplicateUser.temporaryUserId ? ' · 잠재회원' : ''}
              </Typography>
            ))}
          </Box>
          {duplicateUsers.length > DUPLICATE_PREVIEW_COUNT && (
            <Typography sx={{ fontSize: '12px', color: '#888', mt: '6px' }}>
              {t('외')} {duplicateUsers.length - DUPLICATE_PREVIEW_COUNT}
              {t('명')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CommonModalButton variant="cancel" onClick={() => setDuplicateConfirmOpen(false)}>
            {t('Cancel')}
          </CommonModalButton>
          <CommonModalButton
            variant="submit"
            onClick={registerTemporaryAndSave}
            disabled={isRegisteringTemporary}>
            {isRegisteringTemporary ? t('등록 중...') : t('등록')}
          </CommonModalButton>
        </DialogActions>
      </Dialog>

      {/* 잠재회원 등록까지 마친 뒤 판매등록으로 이어갈지 확인 */}
      <Dialog open={saleConfirmOpen} onClose={handleSkipSale}>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 700 }}>{t('판매등록')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px' }}>{t('이어서 판매등록도 진행할까요?')}</Typography>
          <Typography sx={{ fontSize: '12px', color: '#888', mt: '8px' }}>
            {t('나중에 잠재회원 상세에서도 등록할 수 있습니다.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <CommonModalButton variant="cancel" onClick={handleSkipSale}>
            {t('안 함')}
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleConfirmSale}>
            {t('판매등록')}
          </CommonModalButton>
        </DialogActions>
      </Dialog>

      {/* 상담에서 이어진 판매등록 — MUI Modal 은 포털로 뜨므로 중첩 마운트해도 무방하다 */}
      {saleTarget && (
        <EnterSaleInfoModal
          userInfo={saleTarget.userInfo}
          userDetail={saleTarget.userDetail}
          open
          hideButton
          onClose={handleSaleModalClose}
          redirectOnTemporarySuccess={false}
          skipTemporaryDupCheck
          closeConfirmMessage={'판매등록을 그만두시겠습니까?\n상담과 잠재회원은 등록되어 있습니다.'}
        />
      )}
      <ConfirmModal
        open={!!duetPartnerAsk}
        message={`${duetPartnerAsk?.memberName || ''} 회원과 듀엣 수강권을 같이 구매했습니다.\n같이 예약할까요?`}
        highlightText="같이 예약할까요?"
        highlightColor="#4AB3BC"
        cancelText="혼자 예약"
        confirmText="같이 예약"
        confirmColor="#4AB3BC"
        onClose={() => setDuetPartnerAsk(null)}
        onConfirm={() => {
          const partner = duetPartnerAsk;
          setDuetPartnerAsk(null);
          setSelectedMembers(prev =>
            prev.length >= 2 || prev.some(member => member.id === partner.memberId)
              ? prev
              : [...prev, { id: partner.memberId, name: partner.memberName }],
          );
        }}
      />
      {/* 바깥 클릭으로 닫을 때 작성 중이면 한 번 묻는다 */}
      <ConfirmModal
        open={discardAsk}
        message={'작성 중인 내용이 사라집니다.\n닫을까요?'}
        highlightText="닫을까요?"
        highlightColor="#4AB3BC"
        cancelText="계속 작성"
        confirmText="닫기"
        confirmColor="#4AB3BC"
        onClose={() => setDiscardAsk(false)}
        onConfirm={() => {
          setDiscardAsk(false);
          handleCancel();
        }}
      />
      <TicketPeriodDialog
        info={periodAlert?.info}
        targetDate={periodAlert?.targetDate}
        actionLabel={periodAlert?.actionLabel}
        onClose={() => setPeriodAlert(null)}
      />
    </>
  );
};

export default PrivateLessonEditModal;
